import { useEffect } from "react";
import { Link } from "react-router-dom";
import { applyPageSeo } from "../../lib/seo";
import { RelatedArticles } from "../../components/RelatedArticles";
import { TableOfContents } from "../../components/TableOfContents";
import { FloatingTableOfContents } from "../../components/FloatingTableOfContents";

const comparisonPoints = [
  {
    feature: "Approach",
    reactEmail: "Component-based JSX — write email templates as React components with full TypeScript support",
    mjml: "XML-like markup language — write MJML tags that compile to responsive HTML",
  },
  {
    feature: "Language",
    reactEmail: "TypeScript / JSX (the same language as your web app)",
    mjml: "MJML (custom XML syntax, separate from your app code)",
  },
  {
    feature: "Ecosystem",
    reactEmail: "React ecosystem — npm packages, hooks, shared components, unit tests",
    mjml: "MJML ecosystem — mjml-components, templates, but limited extensibility",
  },
  {
    feature: "Rendering",
    reactEmail: "Server-side render to HTML via @react-email/render",
    mjml: "Compile MJML → HTML via mjml CLI or API",
  },
  {
    feature: "Testing",
    reactEmail: "Jest/Vitest unit tests, snapshot testing, visual regression",
    mjml: "Visual preview + HTML validation, no unit test story",
  },
  {
    feature: "Customization",
    reactEmail: "Full React — conditional rendering, loops, dynamic props, i18n",
    mjml: "mj-include, mj-style, limited dynamic content via templating engines",
  },
  {
    feature: "Developer experience",
    reactEmail: "Same tooling as your app — VS Code, Prettier, ESLint, TypeScript",
    mjml: "Separate toolchain — MJML linter, preview app, no type safety",
  },
  {
    feature: "Email client support",
    reactEmail: "Good — handles Outlook, Gmail quirks via component internals",
    mjml: "Excellent — battle-tested across 30+ email clients, MJML's core strength",
  },
  {
    feature: "Bundle size",
    reactEmail: "~50KB rendered HTML (tree-shakeable components)",
    mjml: "~30-80KB rendered HTML (depends on template complexity)",
  },
  {
    feature: "Community",
    reactEmail: "Growing fast — 16K+ GitHub stars, Zeno, Resend backing",
    mjml: "Established — 17K+ GitHub stars, Mailjet/RetailNext backing",
  },
];

const whenReactEmail = [
  {
    scenario: "Your product is built with React/Next.js",
    reason: "Same language, same tooling, same team. No context switching between web and email code.",
  },
  {
    scenario: "You need type-safe, testable email templates",
    reason: "TypeScript catches broken props at compile time. Jest catches visual regressions at CI time.",
  },
  {
    scenario: "You want to share components across emails",
    reason: "Build a <Header />, <Footer />, <OrderSummary /> once — reuse across 50 transactional templates.",
  },
  {
    scenario: "Your emails have dynamic, conditional content",
    reason: "React's conditional rendering (ternaries, &&, map) is far more expressive than MJML templating.",
  },
  {
    scenario: "You want AI to generate your templates",
    reason: "LLMs produce better JSX than MJML. React Email's structure maps cleanly to AI generation prompts.",
  },
];

const whenMjml = [
  {
    scenario: "Maximum email client compatibility is critical",
    reason: "MJML has been battle-tested across 30+ email clients for 7+ years. If Outlook 2016 on Windows is your #1 client, MJML has fewer edge cases.",
  },
  {
    scenario: "Your team doesn't write React",
    reason: "If your stack is Python, Go, or Ruby and you just need HTML output, MJML's XML syntax is approachable for non-developers.",
  },
  {
    scenario: "You need a drag-and-drop visual editor",
    reason: "MJML has better visual editor support (MJML App, Bee Free). React Email is code-first.",
  },
  {
    scenario: "You're migrating from legacy email templates",
    reason: "MJML's XML structure maps more naturally to table-based HTML layouts, making migration easier.",
  },
];

const blogTocItems = [
  { id: "feature-comparison", text: "Feature-by-feature comparison", level: 2 },
  { id: "code-examples", text: "Side-by-side code examples", level: 2 },
  { id: "when-react-email", text: "When to pick React Email", level: 2 },
  { id: "when-mjml", text: "When to pick MJML", level: 2 },
  { id: "verdict", text: "Our take", level: 2 },
];

export function ReactEmailVsMjml() {
  useEffect(() => {
    const cleanup = applyPageSeo({
      title: "React Email vs MJML — Which Should You Use in 2026?",
      description:
        "Detailed comparison of React Email and MJML for building email templates. Syntax, DX, testing, ecosystem, and when to pick each. Plus AI generation with React Email Builder.",
      path: "/blog/react-email-vs-mjml",
      keywords: [
        "react email vs mjml",
        "mjml vs react email",
        "react email alternatives",
        "email template framework comparison",
        "best email template tool",
        "mjml react email",
        "email development tools",
      ],
    });

    // Add Article structured data for Google rich results
    const articleJsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "React Email vs MJML — Which Should You Use in 2026?",
      "description": "Detailed comparison of React Email and MJML for building email templates. Syntax, DX, testing, ecosystem, and when to pick each.",
      "url": "https://reactemailbuilder.com/blog/react-email-vs-mjml",
      "datePublished": "2026-04-05",
      "dateModified": "2026-04-19",
      "author": { "@type": "Organization", "name": "React Email Builder" },
      "publisher": { "@type": "Organization", "name": "React Email Builder", "url": "https://reactemailbuilder.com" },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(articleJsonLd);
    script.id = "article-jsonld";
    document.head.appendChild(script);

    return () => {
      cleanup();
      const existing = document.getElementById("article-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="bg-white text-slate-950">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-400 transition hover:text-slate-600">Home</Link>
            <svg aria-hidden="true" className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <Link to="/blog" className="text-slate-400 transition hover:text-slate-600">Blog</Link>
            <svg aria-hidden="true" className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <span className="text-slate-700">React Email vs MJML</span>
          </nav>
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">
            Comparison Guide
          </p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            React Email vs MJML: Which should you use in 2026?
          </h1>
          <p className="mt-6 text-lg leading-8 text-slate-600 sm:text-xl">
            Two very different approaches to building email templates. Here's an honest,
            detailed comparison to help you decide.
          </p>

          {/* Mobile TOC */}
          <div className="mt-8 xl:hidden">
            <TableOfContents items={blogTocItems} />
          </div>
        </div>
      </section>

      <FloatingTableOfContents items={blogTocItems} />

      {/* Comparison Table */}
      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 id="feature-comparison" className="text-3xl font-bold tracking-tight sm:text-4xl">
            Feature-by-feature comparison
          </h2>

          <div className="mt-10 overflow-hidden rounded-md border border-slate-200">
            {/* Header */}
            <div className="grid grid-cols-3 gap-px bg-slate-200 text-sm font-semibold">
              <div className="bg-slate-100 px-4 py-3 text-slate-700">Feature</div>
              <div className="bg-sky-50 px-4 py-3 text-sky-900">React Email</div>
              <div className="bg-purple-50 px-4 py-3 text-purple-900">MJML</div>
            </div>
            {/* Rows */}
            {comparisonPoints.map((row) => (
              <div key={row.feature} className="grid grid-cols-3 gap-px bg-slate-200 text-sm">
                <div className="bg-white px-4 py-4 font-medium text-slate-950">{row.feature}</div>
                <div className="bg-sky-50/50 px-4 py-4 text-slate-700">{row.reactEmail}</div>
                <div className="bg-purple-50/50 px-4 py-4 text-slate-700">{row.mjml}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Code Examples */}
      <section className="border-t border-slate-200 bg-slate-50 py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="code-examples" className="text-3xl font-bold tracking-tight sm:text-4xl">
            Side-by-side code: a simple welcome email
          </h2>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="rounded bg-sky-100 px-3 py-1 text-sm font-semibold text-sky-800">React Email</span>
              </div>
              <pre className="overflow-x-auto rounded-md border border-slate-200 bg-slate-950 p-5 text-sm leading-6 text-slate-300">
{`import { Html, Head, Body, Container,
  Heading, Text, Button }
from "@react-email/components";

export default function WelcomeEmail() {
  return (
    <Html>
      <Head />
      <Body style={main}>
        <Container style={container}>
          <Heading style={h1}>
            Welcome aboard!
          </Heading>
          <Text style={text}>
            Your account is ready. Let's get
            you started with 3 quick steps.
          </Text>
          <Button style={btn}
            href="https://app.example.com">
            Get Started
          </Button>
        </Container>
      </Body>
    </Html>
  );
}`}
              </pre>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="rounded bg-purple-100 px-3 py-1 text-sm font-semibold text-purple-800">MJML</span>
              </div>
              <pre className="overflow-x-auto rounded-md border border-slate-200 bg-slate-950 p-5 text-sm leading-6 text-slate-300">
{`<mjml>
  <mj-head />
  <mj-body>
    <mj-container>
      <mj-section>
        <mj-column>
          <mj-text font-size="24px"
            font-weight="bold">
            Welcome aboard!
          </mj-text>
          <mj-text>
            Your account is ready. Let's
            get you started with 3 quick
            steps.
          </mj-text>
          <mj-button href="https://app.example.com"
            background-color="#2563eb">
            Get Started
          </mj-button>
        </mj-column>
      </mj-section>
    </mj-container>
  </mj-body>
</mjml>`}
              </pre>
            </div>
          </div>

          <div className="mt-6 rounded-md bg-amber-50 border border-amber-200 px-5 py-4">
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Key difference:</span> React Email gives you TypeScript, props, hooks, and the full React toolkit.
              MJML gives you a simpler, more declarative syntax with arguably better Outlook compatibility out of the box.
            </p>
          </div>
        </div>
      </section>

      {/* When to pick React Email */}
      <section className="border-t border-slate-200 bg-white py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="when-react-email" className="text-3xl font-bold tracking-tight sm:text-4xl">
            When to pick React Email
          </h2>

          <div className="mt-8 space-y-4">
            {whenReactEmail.map((item) => (
              <div key={item.scenario} className="rounded-md border border-sky-200 bg-sky-50/50 p-5">
                <h3 className="text-base font-semibold text-sky-900">{item.scenario}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-700">{item.reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-content CTA */}
      <section className="border-y border-slate-200 bg-slate-50 py-8">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:px-8">
            <div>
              <p className="text-sm font-semibold text-slate-950">
                See React Email templates in action
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Browse 148 brand-inspired templates, or generate your own with AI. Free tier available.
              </p>
            </div>
            <Link
              to="/discover/templates"
              className="shrink-0 rounded bg-slate-950 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Browse templates
            </Link>
          </div>
        </div>
      </section>

      {/* When to pick MJML */}
      <section className="border-t border-slate-200 bg-slate-50 py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="when-mjml" className="text-3xl font-bold tracking-tight sm:text-4xl">
            When to pick MJML
          </h2>

          <div className="mt-8 space-y-4">
            {whenMjml.map((item) => (
              <div key={item.scenario} className="rounded-md border border-purple-200 bg-purple-50/50 p-5">
                <h3 className="text-base font-semibold text-purple-900">{item.scenario}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-700">{item.reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Verdict */}
      <section className="border-t border-slate-200 bg-white py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="verdict" className="text-3xl font-bold tracking-tight sm:text-4xl">
            Our take
          </h2>
          <div className="mt-6 space-y-4 text-lg leading-8 text-slate-600">
            <p>
              If your team writes React, <strong className="text-slate-950">pick React Email</strong>.
              The developer experience advantage is enormous — same language, same tooling, same mental model.
              You'll ship faster, catch bugs earlier, and maintain your email templates like any other part of your codebase.
            </p>
            <p>
              If your team doesn't write React and <strong className="text-slate-950">cross-client compatibility is your top priority</strong>,
              MJML is still a solid choice. It's been solving the Outlook problem longer, and its declarative syntax
              is approachable for designers and non-developers.
            </p>
            <p>
              The gap is narrowing. React Email's client compatibility improves every release, and tools like
              React Email Builder make it even easier to start with production-ready templates and AI generation.
            </p>
          </div>
        </div>
      </section>

      <RelatedArticles exclude="/blog/react-email-vs-mjml" />

      {/* CTA */}
      <section className="border-t border-slate-200 bg-slate-950 py-14 text-white sm:py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Try React Email templates today
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-300">
            148 brand-inspired React Email templates. AI generation. Export HTML or JSX. Free tier available.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Link
              to="/discover/templates"
              className="inline-flex items-center gap-2 rounded bg-white px-6 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
            >
              Browse React Email templates
            </Link>
            <Link
              to="/auth/signup?redirect=%2Fbuilder"
              className="inline-flex items-center gap-2 rounded border border-white/40 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition hover:border-white/60 hover:bg-white/15"
            >
              Start building free
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}